/**
 * Twin stages resource (P1.8b) — the org's configured output stages, derived
 * server-side from entities' `data.entity.trackedOutput` config (not
 * hardcoded). Each producing entity yields one Stage describing how its output
 * is identified + QC'd; the Parts screen builds its stage selector from these.
 *
 * Wire shapes mirror the backend GET /v1/twin/stages contract exactly
 * (snake_case). The list payload uses the standard httpx.List
 * `{ items, total, limit, offset }` shape.
 *
 * NOTE: this endpoint returns empty until a server-side backfill runs, so
 * consumers MUST fall back to the static OUTPUT_STAGES default (see
 * parts.tsx) — never assume it is populated.
 */

import { api } from "@/lib/api-client";
import { qs } from "./qs";

/**
 * One configured output stage — the unit-stage descriptor the Parts screen
 * lists. Derived from a producing entity's `trackedOutput` config + its output
 * port's material class.
 */
export interface Stage {
  /** The producing entity instance id. */
  entity_id: string;
  /** The producing entity's display name. */
  entity_name: string;
  /** Material class of the entity's output port — what `tracked_units` filter on. */
  material_class: string;
  /** Heat-scoped id prefix for serialized units (e.g. "MO"). */
  id_prefix: string;
  /** Stage label (e.g. "Mould"). */
  label: string;
  /** True → show in the Parts stage selector. */
  is_part: boolean;
  /** Representation: "discrete_part" (serialized units) vs "bulk_lot" (per-heat lot). */
  kind: "discrete_part" | "bulk_lot";
  /** QC measurements attached to this stage's output. */
  qc_tests: string[];
  /** Defect types this stage's QC can reveal. */
  qc_defects: string[];
}

export const twinStages = {
  /**
   * The org's configured output stages (httpx.List shape). Derived from
   * entities' trackedOutput config; may be EMPTY until a backfill runs
   * server-side — callers fall back to OUTPUT_STAGES.
   */
  stages: (params?: { limit?: number; offset?: number }) =>
    api.get<{ items: Stage[]; total: number; limit: number; offset: number }>(
      `/v1/twin/stages${qs(params)}`,
    ),
};
